import { PDSIconComponent } from '@persona-id/design-tokens/dist/icons/react/16px/types';
import {
  BookOpenIcon,
  ChatIcon,
  EditIcon,
  MailIcon,
  MoveIcon,
  PlusIcon,
  ZapIcon,
} from '@persona-id/design-tokens/icons';
import vendors from 'shared/external_integration/external_integration_vendors.json';

import MastercardIcon from 'images/icons/integrations/Mastercard.svg';
import Auth0Icon from 'images/icons/marketplace/Auth0Icon.svg';
import ChainalysisIcon from 'images/icons/marketplace/ChainalysisIcon.svg';
import ClearbitIcon from 'images/icons/marketplace/ClearbitIcon.svg';
import CoinbaseIcon from 'images/icons/marketplace/CoinbaseIcon.svg';
import EquifaxIcon from 'images/icons/marketplace/EquifaxIcon.svg';
import FullContactIcon from 'images/icons/marketplace/FullContactIcon.svg';
import HubSpotIcon from 'images/icons/marketplace/HubSpotIcon.svg';
import IntercomIcon from 'images/icons/marketplace/IntercomIcon.svg';
import KyckrIcon from 'images/icons/marketplace/KyckrIcon.svg';
import MiddeskIcon from 'images/icons/marketplace/MiddeskIcon.svg';
import MXIcon from 'images/icons/marketplace/MXIcon.svg';
import NovaCreditIcon from 'images/icons/marketplace/NovaCreditIcon.svg';
import OpenAIIcon from 'images/icons/marketplace/OpenAIIcon.svg';
import SalesforceIcon from 'images/icons/marketplace/SalesforceIcon.svg';
import SentiLinkIcon from 'images/icons/marketplace/SentiLinkIcon.svg';
import SlackIcon from 'images/icons/marketplace/SlackIcon.svg';
import TRMIcon from 'images/icons/marketplace/TRMIcon.svg';
import ZendeskIcon from 'images/icons/marketplace/ZendeskIcon.svg';
import { FieldSchema } from 'lib/fields';

enum LegacyVendorAuthType {
  ApiKey = 'api_key',
  Oauth = 'oauth',
}

export enum LegacyVendorType {
  AUTHENTICATION = 'authentication',
  ENRICHMENT = 'enrichment',
  PRODUCTIVITY = 'productivity',
}

// These names should match the names of the vendors in
// app/assets/shared/external_integration/external_integration_vendors.json
export enum LegacyVendorName {
  Auth0 = 'auth0',
  Chainalysis = 'chainalysis',
  Clearbit = 'clearbit',
  Coinbase = 'coinbase',
  Equifax = 'equifax',
  FullContact = 'fullcontact',
  HubSpot = 'hubspot',
  Intercom = 'intercom',
  Kyckr = 'kyckr',
  MastercardIdentity = 'mastercard-identity',
  Middesk = 'middesk',
  MX = 'mx',
  NovaCredit = 'novacredit',
  OpenAI = 'openai',
  Salesforce = 'salesforce',
  SentiLink = 'sentilink',
  Slack = 'slack',
  TRM = 'trm',
  Zendesk = 'zendesk',
}

export enum LegacyVendorUseCaseAction {
  Contact = 'contact',
  Create = 'create',
  Enrich = 'enrich',
  Match = 'match',
  Retrieve = 'retrieve',
  Search = 'search',
  Send = 'send',
  Update = 'update',
}

export const LEGACY_VENDOR_USE_CASE_ICON: Record<LegacyVendorUseCaseAction, PDSIconComponent> = {
  contact: ChatIcon,
  create: PlusIcon,
  enrich: ZapIcon,
  match: MoveIcon,
  retrieve: BookOpenIcon,
  search: BookOpenIcon,
  send: MailIcon,
  update: EditIcon,
};

export const LEGACY_EXTERNAL_INTEGRATION_VENDOR_ICON = (
  vendor: LegacyExternalIntegrationVendor,
) => {
  switch (vendor.name) {
    case LegacyVendorName.Auth0:
      return Auth0Icon;
    case LegacyVendorName.Chainalysis:
      return ChainalysisIcon;
    case LegacyVendorName.Clearbit:
      return ClearbitIcon;
    case LegacyVendorName.Coinbase:
      return CoinbaseIcon;
    case LegacyVendorName.Equifax:
      return EquifaxIcon;
    case LegacyVendorName.FullContact:
      return FullContactIcon;
    case LegacyVendorName.HubSpot:
      return HubSpotIcon;
    case LegacyVendorName.Intercom:
      return IntercomIcon;
    case LegacyVendorName.Kyckr:
      return KyckrIcon;
    // NB(sal): This is a hack to enable Mastercard Integration training. I'll delete this when we cut over to the Integrations tab.
    case LegacyVendorName.MastercardIdentity:
      return MastercardIcon;
    case LegacyVendorName.Middesk:
      return MiddeskIcon;
    case LegacyVendorName.MX:
      return MXIcon;
    case LegacyVendorName.NovaCredit:
      return NovaCreditIcon;
    case LegacyVendorName.OpenAI:
      return OpenAIIcon;
    case LegacyVendorName.Salesforce:
      return SalesforceIcon;
    case LegacyVendorName.SentiLink:
      return SentiLinkIcon;
    case LegacyVendorName.TRM:
      return TRMIcon;
    case LegacyVendorName.Slack:
      return SlackIcon;
    case LegacyVendorName.Zendesk:
      return ZendeskIcon;
    default:
      return ZapIcon;
  }
};
export interface LegacyVendorUseCase {
  action: LegacyVendorUseCaseAction;
  description: string;
}

export interface LegacyVendorOAuthConfig {
  vendor_gscm_id: string;
  site: string;
  authorize_url: string;
  token_url: string;
  scope: string;
}
export interface LegacyVendorClientConfig {
  oauth_config: LegacyVendorOAuthConfig;
}

export interface LegacyExternalIntegrationVendor {
  name: LegacyVendorName;
  'display-name': string;
  description: string;
  'vendor-type'?: LegacyVendorType;
  'authentication-type': LegacyVendorAuthType;
  'site-link': string;
  'input-fields': FieldSchema[];
  'use-cases'?: LegacyVendorUseCase[];
  model: string;
  'additional-info'?: string;
  'client-config'?: LegacyVendorClientConfig;
}

export const LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL = `${location.origin}/oauth_callback`;

export const LEGACY_EXTERNAL_INTEGRATION_VENDORS: LegacyExternalIntegrationVendor[] =
  vendors as LegacyExternalIntegrationVendor[];
